import handlebars from "handlebars";
import { generatePhysicalRiskTemplate, generateRegulatoryRiskTemplate, generateDecarbonizationTemplate, generateSSBBTemplate } from "../templates/sustainabilityTemplate";
;

// Define interfaces for the input data structure
interface PropertyData {
    propertyId: string;
    invId: string;
    portfolioId: string ;
    portfolioName: string ;
    riverFloodDefendedRisk: string ;
    seaLevelRisk: string ;
    wildfireRisk: string ;
    heatStress: string ;
    droughtStress: string ;
    tropicalCyclone: string;
    countOfMitigationMeasures: number;
    mitigationMeasures: string;
    complianceStatusAccToSsbb: string ;
    decarbonizationAssessmentStatus: string ;
    buildingPerformanceStandard: string;
    mitigationPlan: string;
    physicalRiskComment: string;
    exposureToAdditionalRegulatory: string;
    complianceStatusComment: string ;
    includeCostToProcedure: string;
    includeProjectsComesIntoCompliance: string;
    regulatoryRiskComment: string;
    decarbonizationAssessmentStatusComment: string;
    decarbonizationAssessmentComment: string;
    ssbbFeatures: string;
    ssbbStandards: string;
    ssbbStandardsComment: string;
}

interface SustainabilityData {
    sustainabilityData: {
        [key: string]: PropertyData;
    };
}

// Define interfaces for the transformed data structure
interface TransformedProperty {
    propertyId: string;
    physicalRisk: {
        riverFloodDefendedRisk: string;
        seaLevelRisk: string;
        floodRisk: string;
        wildfireRisk: string;
        heatStress: string;
        droughtStress: string;
        tropicalCyclone: string;
        mitigationMeasuresCount: string;
        mitigationPlans: string;
        comments: string;
    };
    regulatoryRisk: {
        buildingPerformanceStandard: string;
        additionalRequirements: string;
        complianceStatus: string;
        complianceComments: string;
        vendorProcurement: string;
        complianceProjects: string;
        comments: string;
    };
    decarbonization: {
        assessmentStatus: string;
        statusComments: string;
        comments: string;
    };
    ssbbStandards: {
        featuresIncluded: string;
        featuresList: string;
        comments: string;
    };
}

interface TransformedData {
    properties: TransformedProperty[];
}

export const sustainabilityTemplate = (sustainabilityData: SustainabilityData): string[] => {
    const compilePhysicalRiskTemplate = handlebars.compile(generatePhysicalRiskTemplate);
    const compileRegulatoryRiskTemplate = handlebars.compile(generateRegulatoryRiskTemplate);
    const compileDecarbonizationTemplate = handlebars.compile(generateDecarbonizationTemplate);
    const compileSSBBTemplate = handlebars.compile(generateSSBBTemplate);

    // Transform the data to match the template structure
    const transformedData: TransformedData = {
        properties: Object.entries(sustainabilityData).map(([_, property]: [string, any]) => ({
            propertyId: property.propertyId,
            physicalRisk: {
                riverFloodDefendedRisk: property.riverFloodDefendedRisk,
                seaLevelRisk: property.seaLevelRisk ,
                floodRisk: property.floodRisk ,
                wildfireRisk: property.wildfireRisk ,
                heatStress: property.heatStress ,
                droughtStress: property.droughtStress ,
                tropicalCyclone: property.tropicalCyclone ,
                mitigationMeasuresCount: property.mitigationMeasures?.split(',').map(measure => {
                    const trimmed = measure.trim();
                    return trimmed.length > 25 ? trimmed.match(/.{1,25}/g)?.join('<br>') || trimmed : trimmed;
                }).join('<br><br>') || '',
                mitigationPlans: property.mitigationPlan ,
                comments: property.physicalRiskComment 
            },
            regulatoryRisk: {
                buildingPerformanceStandard: property.buildingPerformanceStandard ,
                additionalRequirements: property.additionalRegulatoryReq ,
                complianceStatus: property.complianceStatusAccToSsbb ,
                complianceComments: property.complianceStatusComment ,
                vendorProcurement: property.includeCostToProcedure ,
                complianceProjects: property.includeProjectsComesIntoCompliance ,
                comments: property.regulatoryRiskComment 
            },
            decarbonization: {
                assessmentStatus: property.decarbonizationAssessmentStatus ,
                statusComments: property.decarbonizationAssessmentComment ,
                comments: property.decarbonizationAssessmentStatusComment 
            },
            ssbbStandards: {
                featuresIncluded: property.ssbbStandards ,
                featuresList: property.ssbbFeatures ,
                comments: property.ssbbStandardsComment 
            }
        }))
    };

        return [
        compilePhysicalRiskTemplate(transformedData),
        compileRegulatoryRiskTemplate(transformedData),
        compileDecarbonizationTemplate(transformedData),
        compileSSBBTemplate(transformedData)
    ];
};

